import type { RewardClaimResult, RewardOffer } from '../store/services/rewardsApi';

type ClaimRequest = {
  projectId: string; rewardId: string; sessionId?: string | null;
};
export type ClaimReward = (request: ClaimRequest) => { unwrap: () => Promise<RewardClaimResult> };

const inFlightClaims = new Map<string, Promise<RewardClaimResult>>();

export const preciseCredits = (amount: string): string => {
  const [integer, decimal = ''] = amount.split('.');
  const fraction = decimal.replace(/0+$/, '');

  return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''}`;
};

/** Add the server's decimal strings without rounding fractional credits. */
export const totalRewardCredits = (offers: RewardOffer[]): string => {
  const parts = offers.map(({ amount }) => amount.split('.'));
  const precision = Math.max(0, ...parts.map(([, fraction = '']) => fraction.length));
  const total = parts.reduce((sum, [integer, fraction = '']) => (
    sum + globalThis.BigInt(integer + fraction.padEnd(precision, '0'))
  ), globalThis.BigInt(0)).toString().padStart(precision + 1, '0');

  return preciseCredits(precision ? `${total.slice(0, -precision)}.${total.slice(-precision)}` : total);
};

/** A grouped claim and any still-mounted individual control share one request. */
export const claimRewardOnce = (
  claim: ClaimReward, offer: RewardOffer, sessionId: string | null | undefined, onGranted: () => void,
): Promise<RewardClaimResult> => {
  const key = `${offer.project_id}:${offer.id}`;
  const pending = inFlightClaims.get(key);

  if (pending) return pending;
  const request = claim({
    projectId: offer.project_id, rewardId: offer.id, sessionId: sessionId || offer.session_id,
  }).unwrap().then((result) => {
    if (result.already_claimed === false && result.reward.claimed_at
      && result.reward.id === offer.id && result.reward.project_id === offer.project_id) onGranted();

    return result;
  }).finally(() => {
    if (inFlightClaims.get(key) === request) inFlightClaims.delete(key);
  });

  inFlightClaims.set(key, request);

  return request;
};
